import { useEffect, useState } from "react"
import { cn } from "@/lib/utils"
import { Icon } from "@/components/ui/icon"

export type ListRevealCountdown = {
  days: number
  hours: number
  minutes: number
  seconds: number
}

function useListRevealCountdown(listRevealAt: string | null | undefined) {
  const [countdown, setCountdown] = useState<ListRevealCountdown | null>(null)

  useEffect(() => {
    if (!listRevealAt) {
      setCountdown(null)
      return
    }

    const tick = () => {
      const now = new Date()
      const revealDate = new Date(listRevealAt)
      const diff = revealDate.getTime() - now.getTime()

      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 })
        return
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)

      setCountdown({ days, hours, minutes, seconds })
    }

    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [listRevealAt])

  return countdown
}

export function formatListRevealDateLong(iso: string) {
  const revealDate = new Date(iso)
  const dateStr = revealDate.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
  })
  const timeStr = revealDate.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  })
  return `${dateStr} às ${timeStr}`
}

type InterestListRevealPanelProps = {
  listRevealAt: string
  className?: string
  /** Título + data centralizados (ex.: coluna da direita na página do jogo). */
  centerHeader?: boolean
}

/** Bloco “Revelação da Lista” + countdown (mesmo visual em /interest/success e detalhes do jogo). */
export function InterestListRevealPanel({
  listRevealAt,
  className,
  centerHeader = false,
}: InterestListRevealPanelProps) {
  const countdown = useListRevealCountdown(listRevealAt)

  if (!countdown) return null

  return (
    <div
      className={cn(
        "bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/10 rounded-xl border border-blue-200/50 dark:border-blue-500/20 p-6",
        className)}
    >
      <div className="flex flex-col items-center gap-5">
        <div
          className={cn(
            "flex gap-3",
            centerHeader ? "flex-col items-center text-center" : "items-center")}
        >
          <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-500/15 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0">
            <Icon name="schedule" />
          </div>
          <div className={cn(!centerHeader && "text-left")}>
            <p className="text-foreground font-bold">Revelação da Lista</p>
            <p className="text-muted-foreground text-sm">{formatListRevealDateLong(listRevealAt)}</p>
          </div>
        </div>

        <div className="flex items-center justify-center gap-3">
          {countdown.days > 0 && (
            <>
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-xl bg-white dark:bg-card border border-border dark:border-border flex items-center justify-center shadow-sm">
                  <span className="text-2xl font-black text-blue-600 dark:text-blue-400 tabular-nums">
                    {String(countdown.days).padStart(2, "0")}
                  </span>
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mt-2">
                  Dias
                </span>
              </div>
              <span className="text-2xl font-bold text-blue-400 dark:text-blue-500 mb-6">:</span>
            </>
          )}
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-xl bg-white dark:bg-card border border-border dark:border-border flex items-center justify-center shadow-sm">
              <span className="text-2xl font-black text-blue-600 dark:text-blue-400 tabular-nums">
                {String(countdown.hours).padStart(2, "0")}
              </span>
            </div>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mt-2">
              Horas
            </span>
          </div>
          <span className="text-2xl font-bold text-blue-400 dark:text-blue-500 mb-6">:</span>
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-xl bg-white dark:bg-card border border-border dark:border-border flex items-center justify-center shadow-sm">
              <span className="text-2xl font-black text-blue-600 dark:text-blue-400 tabular-nums">
                {String(countdown.minutes).padStart(2, "0")}
              </span>
            </div>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mt-2">Min</span>
          </div>
          <span className="text-2xl font-bold text-blue-400 dark:text-blue-500 mb-6">:</span>
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-xl bg-white dark:bg-card border border-border dark:border-border flex items-center justify-center shadow-sm">
              <span className="text-2xl font-black text-blue-600 dark:text-blue-400 tabular-nums">
                {String(countdown.seconds).padStart(2, "0")}
              </span>
            </div>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mt-2">Seg</span>
          </div>
        </div>
      </div>
    </div>
  )
}
